"use client";

import { useState, useEffect, useRef } from "react";
import { FileText, CheckCircle2, Loader2, AlertTriangle } from "lucide-react";
import mammoth from "mammoth";

const WORD_STYLES = `
  .word-document-container {
    padding: 24px 16px 40px;
    background: #e5e7eb;
    min-height: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
  }
  .word-document-page {
    background: #fff;
    width: 100%;
    max-width: 800px;
    min-height: 29.7cm;
    padding: 2.54cm 2.54cm 2.54cm 3.17cm;
    box-shadow: 0 2px 12px rgba(0,0,0,0.15);
    position: relative;
    overflow: hidden;
    color: #000;
    font-family: 'Calibri', 'Arial', sans-serif;
    font-size: 11pt;
  }
  
  @media (max-width: 640px) {
    .word-document-page {
      padding: 1.5rem;
      min-height: auto;
    }
  }

  .word-document-page p { margin: 0 0 6pt; line-height: 1.15; }
  .word-document-page h1 { font-size: 16pt; font-weight: bold; margin: 12pt 0 6pt; }
  .word-document-page h2 { font-size: 13pt; font-weight: bold; margin: 10pt 0 6pt; }
  .word-document-page h3 { font-size: 12pt; font-weight: bold; margin: 8pt 0 6pt; }
  .word-document-page h4, .word-document-page h5, .word-document-page h6 { font-size: 11pt; font-weight: bold; margin: 6pt 0 4pt; }
  .word-document-page ul, .word-document-page ol { margin: 0 0 6pt 0; padding-left: 36pt; }
  .word-document-page li { margin-bottom: 2pt; }
  .word-document-page a { color: #1155CC; }
  .word-document-page table {
    border-collapse: collapse;
    width: 100%;
    margin: 8pt 0;
    font-size: 10pt;
  }
  .word-document-page td, .word-document-page th {
    border: 1px solid #000;
    padding: 4pt 6pt;
    vertical-align: top;
  }
  .word-document-page th {
    background: #f2f2f2;
    font-weight: bold;
  }
  .word-document-page img {
    max-width: 100%;
    height: auto;
    display: block;
  }
  .word-document-page .center { text-align: center; }
  .word-document-page .right { text-align: right; }
`;

/**
 * Visor de documentos Word con control de scroll nativo usando mammoth.js
 * Descarga el docx, lo convierte a HTML y detecta si el usuario llegó al final
 */
export default function WordViewer({ 
  wordUrl, 
  docxBase64,
  onScrollComplete,
  className = "" 
}) {
  const [hasScrolledToBottom, setHasScrolledToBottom] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [showManualConfirm, setShowManualConfirm] = useState(false);
  const [docHtml, setDocHtml] = useState("");
  const [error, setError] = useState(null);
  const scrollContainerRef = useRef(null);

  useEffect(() => {
    // Si tenemos la data base64 directamente, la convertimos sin necesidad de fetch (evita problemas de CORS)
    if (docxBase64) {
      const loadFromBase64 = async () => {
        try {
          setIsLoading(true);
          setError(null);

          // Decodificar base64 → ArrayBuffer
          const binaryString = atob(docxBase64);
          const bytes = new Uint8Array(binaryString.length);
          for (let i = 0; i < binaryString.length; i++) {
            bytes[i] = binaryString.charCodeAt(i);
          }
          const arrayBuffer = bytes.buffer;

          const result = await mammoth.convertToHtml(
            { arrayBuffer },
            {
              styleMap: [
                "p[style-name='Heading 1'] => h1:fresh",
                "p[style-name='Heading 2'] => h2:fresh",
                "p[style-name='Heading 3'] => h3:fresh",
                "b => strong",
                "i => em",
                "u => u",
              ],
              includeDefaultStyleMap: true,
              convertImage: mammoth.images.imgElement((image) => {
                return image.read('base64').then((base64Data) => ({
                  src: `data:${image.contentType};base64,${base64Data}`,
                }));
              }),
            }
          );

          setDocHtml(result.value);
        } catch (err) {
          console.error("Error al convertir base64 a HTML:", err);
          setError("Error al procesar la vista previa del contrato.");
          setShowManualConfirm(true);
        } finally {
          setIsLoading(false);
        }
      };

      loadFromBase64();
      return;
    }

    if (!wordUrl) return;

    const loadAndConvert = async () => {
      try {
        setIsLoading(true);
        setError(null);

        const response = await fetch(wordUrl);
        if (!response.ok) {
          throw new Error("No se pudo descargar el archivo de contrato.");
        }
        const arrayBuffer = await response.arrayBuffer();

        const result = await mammoth.convertToHtml(
          { arrayBuffer },
          {
            styleMap: [
              "p[style-name='Heading 1'] => h1:fresh",
              "p[style-name='Heading 2'] => h2:fresh",
              "p[style-name='Heading 3'] => h3:fresh",
              "b => strong",
              "i => em",
              "u => u",
            ],
            includeDefaultStyleMap: true,
            convertImage: mammoth.images.imgElement((image) => {
              return image.read('base64').then((base64Data) => ({
                src: `data:${image.contentType};base64,${base64Data}`,
              }));
            }),
          }
        );

        setDocHtml(result.value);
      } catch (err) {
        console.error("Error al cargar/convertir Word:", err);
        setError("Error al cargar el visor del contrato. Puedes descargarlo y confirmar manualmente.");
        setShowManualConfirm(true);
      } finally {
        setIsLoading(false);
      }
    };

    loadAndConvert();
  }, [wordUrl, docxBase64]);

  // Verificar si no hay scrollbar (el contenido cabe completo)
  // Espera a que las imágenes del documento carguen para medir con precisión
  useEffect(() => {
    if (isLoading || !docHtml) return;

    const container = scrollContainerRef.current;
    if (!container) return;

    const doCheck = () => {
      const clientHeight = container.clientHeight;
      const scrollHeight = container.scrollHeight;

      // Solo proceder si el contenedor tiene dimensiones reales y el contenido
      // es significativamente mayor que 0 (evitar falsos positivos en renders iniciales)
      if (clientHeight > 50 && scrollHeight > 50) {
        // Si el contenido cabe sin scroll (con margen de 10px)
        if (scrollHeight <= clientHeight + 10 && !hasScrolledToBottom) {
          setHasScrolledToBottom(true);
          if (onScrollComplete) {
            onScrollComplete();
          }
        }
      }
    };

    // Esperar a que todas las imágenes del documento carguen antes de medir
    const images = container.querySelectorAll("img");
    let loadedCount = 0;

    const onImageLoaded = () => {
      loadedCount++;
      if (loadedCount >= images.length) {
        // Pequeño retraso extra para que el navegador pinte
        requestAnimationFrame(() => {
          setTimeout(doCheck, 100);
        });
      }
    };

    if (images.length === 0) {
      // Sin imágenes: esperar un frame de render + delay
      requestAnimationFrame(() => {
        setTimeout(doCheck, 300);
      });
    } else {
      images.forEach((img) => {
        if (img.complete) {
          onImageLoaded();
        } else {
          img.addEventListener("load", onImageLoaded, { once: true });
          img.addEventListener("error", onImageLoaded, { once: true });
        }
      });
    }
  }, [isLoading, docHtml]); // eslint-disable-line react-hooks/exhaustive-deps

  // Detección de scroll en el contenedor
  const handleScroll = (e) => {
    const element = e.target;
    const scrolledToBottom = 
      element.scrollHeight - element.scrollTop <= element.clientHeight + 50; // 50px de margen

    if (scrolledToBottom && !hasScrolledToBottom) {
      setHasScrolledToBottom(true);
      if (onScrollComplete) {
        onScrollComplete();
      }
    }
  };

  // Si el usuario confirma manualmente que leyó el documento

  const handleManualConfirm = () => {
    setHasScrolledToBottom(true);
    if (onScrollComplete) {
      onScrollComplete();
    }
  };

  return (
    <div className={`flex flex-col min-h-0 ${className}`}>
      <style>{WORD_STYLES}</style>

      {/* Indicador de progreso */}
      {/* <div className="bg-blue-50 border-l-4 border-blue-500 p-4 mb-4 flex-shrink-0">
        <div className="flex items-start gap-3">
          <FileText className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
          <div className="flex-1">
            <p className="text-sm font-medium text-blue-900">
              {hasScrolledToBottom ? (
                <span className="flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-green-600" />
                  Has revisado el documento completo
                </span>
              ) : (
                "Desliza hacia abajo para leer todo el contrato"
              )}
            </p>
            <p className="text-xs text-blue-700 mt-1">
              {hasScrolledToBottom 
                ? "Ahora puedes proceder con la aceptación del contrato."
                : "Debes leer todo el documento antes de poder aceptarlo."
              }
            </p>
          </div>
        </div>
      </div> */}

      {/* Visor del documento */}
      <div 
        ref={scrollContainerRef}
        onScroll={handleScroll}
        className="flex-1 bg-gray-100 rounded-lg shadow-inner overflow-auto relative min-h-0"
      >
        {isLoading && (
          <div className="absolute inset-0 flex items-center justify-center bg-white z-10">
            <div className="text-center">
              <Loader2 className="w-12 h-12 text-blue-600 animate-spin mx-auto mb-4" />
              <p className="text-gray-600 font-medium">Cargando documento...</p>
              <p className="text-sm text-gray-500 mt-2">Esto puede tomar unos segundos</p>
            </div>
          </div>
        )}

        {/* Contenido renderizado del Word */}
        {!isLoading && docHtml && (
          <div className="word-document-container">
            <div 
              className="word-document-page"
              dangerouslySetInnerHTML={{ __html: docHtml }}
            />
          </div>
        )}

        {/* Error de carga */}
        {error && !isLoading && (
          <div className="absolute inset-0 flex items-center justify-center bg-gray-50 p-6 text-center">
            <div className="max-w-md">
              <AlertTriangle className="w-12 h-12 text-amber-500 mx-auto mb-3" />
              <p className="text-gray-700 font-medium mb-1">{error}</p>
              <p className="text-sm text-gray-500 mb-4">
                Puedes descargar el archivo Word para revisarlo.
              </p>
            </div>
          </div>
        )}

        {/* Confirmación manual si el visor no funciona */}
        {showManualConfirm && !hasScrolledToBottom && (
          <div className="absolute bottom-4 right-4 z-20">
            <div className="bg-white rounded-lg shadow-lg p-4 border border-gray-200 max-w-xs">
              <p className="text-sm text-gray-700 mb-3">
                ¿Ya descargaste y revisaste el documento completo?
              </p>
              <div className="flex gap-2">
                <a
                  href={wordUrl}
                  download
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex-1 px-3 py-2 bg-gray-200 hover:bg-gray-300 text-gray-800 text-xs font-medium rounded transition-colors text-center flex items-center justify-center gap-1"
                >
                  📥 Descargar
                </a>
                <button
                  onClick={handleManualConfirm}
                  className="flex-1 px-3 py-2 bg-green-600 hover:bg-green-700 text-white text-xs font-medium rounded transition-colors"
                >
                  ✓ Ya lo leí
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Indicador visual de scroll */}
      {!hasScrolledToBottom && !isLoading && (
        <div className="mt-4 text-center flex-shrink-0">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-amber-50 border border-amber-200 rounded-full text-sm text-amber-800">
            <svg 
              className="w-4 h-4 animate-bounce" 
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path 
                strokeLinecap="round" 
                strokeLinejoin="round" 
                strokeWidth={2} 
                d="M19 14l-7 7m0 0l-7-7m7 7V3" 
              />
            </svg>
            <span>Desplázate hacia abajo para continuar</span>
          </div>
        </div>
      )}
    </div>
  );
}
